import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Trash2, Power, ShieldCheck, RefreshCw,
  AlertTriangle, Loader2, UserCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending:  { label: 'Väntar godkännande', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)' },
  active:   { label: 'Aktiv',              color: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },
  inactive: { label: 'Inaktiv',            color: '#9ca3af', bg: 'rgba(156, 163, 175, 0.15)' },
};

const FILTERS = [
  ['pending',  'Väntar godkännande', ['pending']],
  ['active',   'Aktiva',             ['active']],
  ['inactive', 'Inaktiva',           ['inactive']],
  ['all',      'Alla',               null],
];

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
}

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 600, color: cfg.color, background: cfg.bg,
    }} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </span>
  );
}

function ConfirmModal({ open, title, body, confirmLabel = 'Bekräfta', danger, onConfirm, onCancel, busy }) {
  if (!open) return null;
  return (
    <div className="ap-modal-overlay" data-testid="user-confirm-modal">
      <div className="ap-modal">
        <div className="ap-modal-icon"><AlertTriangle size={22} /></div>
        <h3 className="ap-modal-title">{title}</h3>
        <p className="ap-modal-body">{body}</p>
        <div className="ap-modal-actions">
          <button className="ap-btn-pill" onClick={onCancel} disabled={busy} data-testid="user-confirm-cancel">
            Avbryt
          </button>
          <button
            className={`ap-btn-pill ${danger ? 'ap-btn-pill--danger' : ''}`}
            onClick={onConfirm}
            disabled={busy}
            data-testid="user-confirm-ok"
          >
            {busy ? <><Loader2 size={12} className="ap-progress-spin" /> Arbetar...</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsersTab() {
  const { session } = useAuth();
  const accessToken = session?.access_token;

  const [users, setUsers] = useState([]);
  const [meId, setMeId]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [busy, setBusy] = useState({});
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    if (!accessToken) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error((await r.json()).detail || r.status);
      const data = await r.json();
      setUsers(data.users || []);
      setMeId(data.me || null);
    } catch (e) {
      toast.error(`Kunde inte hämta användare: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const c = {};
    FILTERS.forEach(([k, , statuses]) => {
      c[k] = statuses ? users.filter(u => statuses.includes(u.status)).length : users.length;
    });
    return c;
  }, [users]);

  const filtered = useMemo(() => {
    const f = FILTERS.find(x => x[0] === activeFilter);
    if (!f || !f[2]) return users;
    return users.filter(u => f[2].includes(u.status));
  }, [users, activeFilter]);

  const callAdmin = async (path, init = {}) => {
    const r = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: {
        ...(init.headers || {}),
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({ detail: r.status }));
      throw new Error(err.detail || `HTTP ${r.status}`);
    }
    return r.json().catch(() => ({}));
  };

  const setRowBusy = (id, v) => setBusy(prev => ({ ...prev, [id]: v }));

  const approve = async (u) => {
    setRowBusy(u.user_id, true);
    try {
      await callAdmin(`/api/admin/users/${u.user_id}/approve`, { method: 'POST' });
      toast.success(`${u.email} godkänd`);
      await load();
    } catch (e) {
      toast.error(`Godkännande misslyckades: ${e.message}`);
    } finally {
      setRowBusy(u.user_id, false);
    }
  };

  const updateRole = async (u, role) => {
    setRowBusy(u.user_id, true);
    try {
      await callAdmin(`/api/admin/users/${u.user_id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      toast.success(`Roll uppdaterad: ${u.email} → ${role}`);
      await load();
    } catch (e) {
      toast.error(`Roll-ändring misslyckades: ${e.message}`);
    } finally {
      setRowBusy(u.user_id, false);
    }
  };

  const updateStatus = async (u, status) => {
    setRowBusy(u.user_id, true);
    try {
      await callAdmin(`/api/admin/users/${u.user_id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      toast.success(`Status uppdaterad: ${u.email} → ${status}`);
      await load();
    } catch (e) {
      toast.error(`Status-ändring misslyckades: ${e.message}`);
    } finally {
      setRowBusy(u.user_id, false);
    }
  };

  const reject = async (u) => {
    setRowBusy(u.user_id, true);
    try {
      await callAdmin(`/api/admin/users/${u.user_id}/reject`, { method: 'POST' });
      toast.success(`${u.email} avvisad och raderad`);
      await load();
    } catch (e) {
      toast.error(`Avvisning misslyckades: ${e.message}`);
    } finally {
      setRowBusy(u.user_id, false);
      setModal(null);
    }
  };

  const remove = async (u) => {
    setRowBusy(u.user_id, true);
    try {
      await callAdmin(`/api/admin/users/${u.user_id}`, { method: 'DELETE' });
      toast.success(`${u.email} borttagen`);
      await load();
    } catch (e) {
      toast.error(`Borttagning misslyckades: ${e.message}`);
    } finally {
      setRowBusy(u.user_id, false);
      setModal(null);
    }
  };

  return (
    <div className="admin-section" data-testid="admin-users-tab">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h2 className="admin-section-title" style={{ margin: 0 }}>Användare</h2>
        <button onClick={load} className="ap-queue-refresh" title="Uppdatera" data-testid="users-refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Filter pills */}
      <div className="ap-queue-filters" style={{ marginBottom: 14 }}>
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            data-testid={`users-filter-${key}`}
            className={`ap-filter-pill${activeFilter === key ? ' ap-filter-pill--active' : ''}`}
            onClick={() => setActiveFilter(key)}
          >
            {label} <span className="ap-filter-count">{counts[key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-loading">Laddar användare...</div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty" data-testid="users-empty">Inga användare i denna vy.</div>
      ) : (
        <div className="admin-table" data-testid="users-table">
          <div className="admin-table-header" style={{ gridTemplateColumns: '1.6fr 0.8fr 1fr 1fr 1fr 1.4fr' }}>
            <span>E-post</span>
            <span>Roll</span>
            <span>Status</span>
            <span>Registrerad</span>
            <span>Senast inloggad</span>
            <span style={{ textAlign: 'right' }}>Åtgärder</span>
          </div>
          {filtered.map(u => {
            const isMe = u.user_id === meId;
            const rowBusy = !!busy[u.user_id];
            return (
              <div
                key={u.user_id}
                className="admin-table-row"
                style={{ gridTemplateColumns: '1.6fr 0.8fr 1fr 1fr 1fr 1.4fr', opacity: rowBusy ? 0.6 : 1 }}
                data-testid={`user-row-${u.email}`}
              >
                <span style={{ fontWeight: 500 }}>
                  {u.email}
                  {isMe && <span style={{ marginLeft: 6, fontSize: 10, color: '#6366f1' }}>(du)</span>}
                </span>
                <span>
                  {u.status === 'active' ? (
                    <select
                      value={u.role}
                      disabled={isMe || rowBusy}
                      onChange={(e) => updateRole(u, e.target.value)}
                      data-testid={`role-select-${u.email}`}
                      style={{
                        background: 'transparent', border: '1px solid var(--border)',
                        color: 'var(--text)', padding: '3px 6px', borderRadius: 6, fontSize: 12,
                      }}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{u.role}</span>
                  )}
                </span>
                <span><StatusBadge status={u.status} /></span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{formatDate(u.created_at)}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{formatDate(u.last_sign_in_at)}</span>
                <span style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                  {u.status === 'pending' && !isMe && (
                    <>
                      <button
                        className="ap-btn-pill"
                        style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' }}
                        onClick={() => approve(u)}
                        disabled={rowBusy}
                        data-testid={`approve-${u.email}`}
                      >
                        <CheckCircle2 size={12} /> Godkänn
                      </button>
                      <button
                        className="ap-btn-pill ap-btn-pill--danger"
                        onClick={() => setModal({ type: 'reject', user: u })}
                        disabled={rowBusy}
                        data-testid={`reject-${u.email}`}
                      >
                        <XCircle size={12} /> Avvisa
                      </button>
                    </>
                  )}
                  {u.status === 'active' && !isMe && (
                    <>
                      <button
                        className="ap-btn-pill"
                        onClick={() => setModal({ type: 'deactivate', user: u })}
                        disabled={rowBusy}
                        data-testid={`deactivate-${u.email}`}
                      >
                        <Power size={12} /> Inaktivera
                      </button>
                      <button
                        className="ap-btn-pill ap-btn-pill--danger"
                        onClick={() => setModal({ type: 'delete', user: u })}
                        disabled={rowBusy}
                        data-testid={`delete-${u.email}`}
                      >
                        <Trash2 size={12} /> Ta bort
                      </button>
                    </>
                  )}
                  {u.status === 'inactive' && !isMe && (
                    <>
                      <button
                        className="ap-btn-pill"
                        style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e' }}
                        onClick={() => updateStatus(u, 'active')}
                        disabled={rowBusy}
                        data-testid={`activate-${u.email}`}
                      >
                        <UserCheck size={12} /> Aktivera
                      </button>
                      <button
                        className="ap-btn-pill ap-btn-pill--danger"
                        onClick={() => setModal({ type: 'delete', user: u })}
                        disabled={rowBusy}
                        data-testid={`delete-${u.email}`}
                      >
                        <Trash2 size={12} /> Ta bort
                      </button>
                    </>
                  )}
                  {isMe && (
                    <span style={{ fontSize: 11, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <ShieldCheck size={12} /> Skyddad
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={modal?.type === 'reject'}
        title="Avvisa ansökan?"
        body={`${modal?.user?.email} kommer att raderas permanent från systemet. Detta går inte att ångra.`}
        confirmLabel="Avvisa & radera"
        danger
        busy={!!busy[modal?.user?.user_id]}
        onConfirm={() => reject(modal.user)}
        onCancel={() => setModal(null)}
      />
      <ConfirmModal
        open={modal?.type === 'deactivate'}
        title="Inaktivera användare?"
        body={`${modal?.user?.email} kommer inte längre kunna logga in, men kontot raderas inte.`}
        confirmLabel="Inaktivera"
        busy={!!busy[modal?.user?.user_id]}
        onConfirm={() => { updateStatus(modal.user, 'inactive'); setModal(null); }}
        onCancel={() => setModal(null)}
      />
      <ConfirmModal
        open={modal?.type === 'delete'}
        title="Ta bort användare?"
        body={`${modal?.user?.email} och all deras data kommer att raderas permanent. Detta går inte att ångra.`}
        confirmLabel="Ta bort permanent"
        danger
        busy={!!busy[modal?.user?.user_id]}
        onConfirm={() => remove(modal.user)}
        onCancel={() => setModal(null)}
      />
    </div>
  );
}
